import type { ApiInputToken, VaultRewards } from '@morpho-org/dapp-core'
import type { InferContractRouterOutputs } from '@orpc/contract'
import type { Address } from 'viem'

import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { parseApiAsset } from '@morpho-org/dapp-core'

import type { CollateralExposure } from '@/components/common/CollateralExposureStack'
import type { UnifiedVaultWarning } from '@/helpers/warnings'
import type { Contract } from '@/lib/orpc/contracts'

import { getParsedValue } from '@/helpers/formatters'
import { VaultV2WarningLevel } from '@/types/enums'

export type VaultV2PositionManagerOutput =
  InferContractRouterOutputs<Contract>['vaultV2']['positionManager']

/**
 * Reward and exposure labels carry no decimals when Core does not know the token. Neither row
 * formats a token amount, so any value works for the token they build.
 */
const LABEL_DECIMALS_FALLBACK = 18

export interface VaultV2PositionManagerData {
  address: VaultV2PositionManagerOutput['address']
  name: string
  listed: boolean
  asset: ApiInputToken
  gates: Record<
    'sendSharesGate' | 'receiveSharesGate' | 'sendAssetsGate' | 'receiveAssetsGate',
    { address: Address | null }
  >
  warnings: UnifiedVaultWarning[]
  grossApy: number | null
  netApy: number | null
  netApyExcludingRewards: number | null
  maxApy: number | null
  performanceFee: number
  managementFee: number | null
  /** The max APY; `0n` when the max rate is zero, which the APY hover card warns about. */
  maxRate: bigint | number | null
  rewards: VaultRewards[]
  totalAssetsUsd: bigint | null
  collateralExposures: CollateralExposure[]
}

const toWarningLevel = (level: string) => {
  switch (level) {
    case 'red':
      return VaultV2WarningLevel.Red
    case 'yellow':
      return VaultV2WarningLevel.Yellow
    case 'green':
      return VaultV2WarningLevel.Green
    default:
      return null
  }
}

/**
 * Maps `vaultV2.positionManager` to what the position-manager dialogs and their transaction
 * card read. Null when the vault asset has no decimals: no amount can be entered then.
 */
export function toVaultV2PositionManagerData(
  vault: VaultV2PositionManagerOutput,
  chainId: number
): VaultV2PositionManagerData | null {
  const { asset } = vault
  if (asset.decimals === null) return null

  const chain = { id: chainId }
  const label = (token: {
    address: Address
    symbol: string | null
    decimals?: number | null
    logoUri: string | null
  }) => ({
    address: token.address,
    symbol: token.symbol ?? '',
    name: token.symbol ?? '',
    decimals: token.decimals ?? LABEL_DECIMALS_FALLBACK,
    logoURI: token.logoUri,
    chain
  })

  const warnings: UnifiedVaultWarning[] = []
  for (const warning of vault.warnings) {
    const level = toWarningLevel(warning.level)
    if (level) warnings.push({ type: warning.type, level })
  }

  return {
    address: vault.address,
    name: vault.name ?? '',
    listed: vault.listed,
    asset: {
      address: asset.address,
      symbol: asset.symbol ?? '',
      name: asset.name ?? asset.symbol ?? '',
      decimals: asset.decimals,
      logoURI: asset.logoUri,
      priceUsd: asset.priceUsd,
      chain,
      yield: asset.yieldApy === null ? null : { apy: asset.yieldApy },
      vault: asset.vault && {
        address: asset.vault.address,
        name: asset.vault.name ?? undefined,
        asset: { logoURI: asset.vault.assetLogoUri },
        state: { avgNetApy: asset.vault.netApy }
      }
    },
    gates: {
      sendSharesGate: { address: vault.gates.sendShares },
      receiveSharesGate: { address: vault.gates.receiveShares },
      sendAssetsGate: { address: vault.gates.sendAssets },
      receiveAssetsGate: { address: vault.gates.receiveAssets }
    },
    warnings,
    grossApy: vault.apy,
    netApy: vault.netApy,
    netApyExcludingRewards: vault.netApyExcludingRewards,
    maxApy: vault.maxApy,
    performanceFee: vault.performanceFee,
    managementFee: vault.managementFee,
    maxRate: vault.maxApy === null || vault.maxApy === 0 ? 0n : vault.maxApy,
    rewards: vault.rewards.map(reward => ({
      supplyApr: reward.supplyApr,
      asset: { ...label(reward.token), chain: { id: reward.token.chainId } }
    })),
    totalAssetsUsd: getParsedValue(vault.totalAssetsUsd, safeParseNumber) ?? null,
    collateralExposures: vault.collateralExposures.map(exposure => ({
      token: parseApiAsset(label(exposure.token)),
      supplyAssetsUsd: getParsedValue(exposure.allocatedAssetsUsd, safeParseNumber) ?? null,
      isIdle: exposure.isIdle
    }))
  }
}
